import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Select,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Popconfirm,
  Badge,
  Statistic,
  Card,
  Divider,
  InputNumber
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  TeamOutlined,
  ExportOutlined,
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined,
  HomeOutlined,
  IdcardOutlined,
  EnvironmentOutlined
} from '@ant-design/icons';
import axios from 'axios';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Empresas = () => {

  const renderFlag = (codigoISO) => {
    if (!codigoISO || codigoISO.trim() === '') {
      return <span style={{ marginRight: '8px' }}>🌍</span>;
    }
    return <span className={`fi fi-${codigoISO.toLowerCase()}`} style={{ marginRight: '8px' }} />;
  };
  const [paises, setPaises] = useState([]);  
    

  // Estados
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [currentCliente, setCurrentCliente] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);

  // Estados para filtros y ordenación
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});

  // Obtener lista de países únicos para filtros
  const paisesUnicos = [...new Set(clientes.map(c => c.Pais).filter(Boolean))];

  // Configuración de columnas de la tabla
  const columns = [
    {
      title: 'Código',
      dataIndex: 'Codigo',
      key: 'Codigo',
      width: 100,
      sorter: (a, b) => a.Codigo?.localeCompare(b.Codigo || '') || 0,
      sortOrder: sortedInfo.columnKey === 'Codigo' && sortedInfo.order,
      render: (codigo) => (
        <Tag 
          color="blue" 
          style={{ 
            fontWeight: 'bold', 
            fontSize: '14px',
            padding: '4px 8px'
          }}
        >
          {codigo}
        </Tag>
      ),
      fixed: 'left',
    },
    {
      title: 'Nombre / Razón Social',
      dataIndex: 'Nombre',
      key: 'Nombre',
      width: 250,
      sorter: (a, b) => a.Nombre?.localeCompare(b.Nombre || '') || 0,
      sortOrder: sortedInfo.columnKey === 'Nombre' && sortedInfo.order,
      render: (nombre, record) => (
        <div style={{ maxWidth: 250 }}>
          <Text strong style={{ fontSize: '13px' }}>
            {nombre}
          </Text>
          {record.email && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: 2 }}>
              <MailOutlined style={{ marginRight: 4 }} />
              {record.email}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'País',
      dataIndex: 'Pais',
      key: 'Pais',
      width: 120,
      sorter: (a, b) => a.Pais?.localeCompare(b.Pais || '') || 0,
      sortOrder: sortedInfo.columnKey === 'Pais' && sortedInfo.order,
      render: (pais) => (
        <Tag color="green" style={{ fontSize: '13px' }}>
          <GlobalOutlined style={{ marginRight: 4 }} />
          {pais || 'N/A'}
        </Tag>
      ),
      filters: paisesUnicos.map(pais => ({ text: pais, value: pais })),
      filteredValue: filteredInfo.Pais || null,
      onFilter: (value, record) => record.Pais === value,
    },
    {
      title: 'Población',
      dataIndex: 'Poblacion',
      key: 'Poblacion',
      width: 150,
      sorter: (a, b) => a.Poblacion?.localeCompare(b.Poblacion || '') || 0,
      sortOrder: sortedInfo.columnKey === 'Poblacion' && sortedInfo.order,
      render: (poblacion) => poblacion || 'N/A',
    },
    {
      title: 'NIF / CIF',
      dataIndex: 'NIF',
      key: 'NIF',
      width: 140,
      sorter: (a, b) => a.NIF?.localeCompare(b.NIF || '') || 0,
      sortOrder: sortedInfo.columnKey === 'NIF' && sortedInfo.order,
      render: (nif) => (
        <Tag color="purple" style={{ fontFamily: 'monospace' }}>
          <IdcardOutlined style={{ marginRight: 4 }} />
          {nif || 'N/A'}
        </Tag>
      ),
    },
    {
      title: 'Envía Factura PDF',
      dataIndex: 'EnviaFacturaPDF',
      key: 'EnviaFacturaPDF',
      width: 140,
      render: (enviaFacturaPDF) => (
        enviaFacturaPDF ? (
          <Badge
            status="success"
            text="Sí"
            style={{ color: '#52c41a', fontWeight: 'bold' }}
          />
        ) : (
          <Badge
            status="default"
            text="No"
            style={{ color: '#8c8c8c', fontWeight: 'bold' }}
          />
        )
      ),
      filters: [
        { text: 'Sí', value: true },
        { text: 'No', value: false },
      ],
      filteredValue: filteredInfo.EnviaFacturaPDF || null,
      onFilter: (value, record) => record.EnviaFacturaPDF === value,
    },
    {
      title: 'Serie PDF',
      dataIndex: 'SeriesEnviaFacturaPDF',
      key: 'SeriesEnviaFacturaPDF',
      width: 100,
      render: (serie) => (
        serie ? (
          <Tag color="orange">{serie}</Tag>
        ) : (
          <Tag color="default">-</Tag>
        )
      ),
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Ver detalles">
            <Button
              type="default"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
            />
          </Tooltip>
          
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          
          <Popconfirm
            title="¿Eliminar cliente?"
            description="Esta acción eliminará el cliente permanentemente"
            onConfirm={() => handleDelete(record.Codigo)}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Eliminar">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Cargar datos iniciales
  useEffect(() => {
    fetchClientes();
  }, []);

  const fetchClientes = async (params = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      const queryParams = {
        search: searchText,
        sortField: params.sortField || sortedInfo.field || 'Codigo',
        sortOrder: params.sortOrder || (sortedInfo.order === 'descend' ? 'DESC' : 'ASC')
      };
      
      // Añadir filtros
      if (filteredInfo.Pais) {
        queryParams.Pais = filteredInfo.Pais[0];
      }
      if (filteredInfo.EnviaFacturaPDF) {
        queryParams.EnviaFacturaPDF = filteredInfo.EnviaFacturaPDF[0];
      }
      
      const response = await axios.get(`${BASE_URL}/api/empresas`, {
        headers: { Authorization: `Bearer ${token}` },
        params: queryParams
      });

      const cleanedData = (response.data.data || []).map(item => ({
        ...item,
        Nombre: item.Nombre || '',
        Telefono: item.Telefono || '',
        Direccion: item.Direccion || '',
        Poblacion: item.Poblacion || '',
        CodPostal: item.CodPostal || '',
        NIF: item.NIF || '',
        Provincia: item.Provincia || '',
        email: item.email || '',
        EnviaFacturaPDF: Boolean(item.EnviaFacturaPDF),
        SeriesEnviaFacturaPDF: item.SeriesEnviaFacturaPDF || ''
      }));
      
      setClientes(cleanedData);

      const responsePaises = await axios.get(`${BASE_URL}/api/paises`, {
        headers: { Authorization: `Bearer ${token}` }
      });      

      setPaises(responsePaises.data.data)            
      
    } catch (error) {
      console.error('❌ Error cargando clientes:', error);
      message.error('Error al cargar los clientes');
      setClientes([]);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  // Handlers para las acciones
  const handleCreate = async () => {
    setModalType('create');
    setCurrentCliente(null);
    form.resetFields();
    
    form.setFieldsValue({
      EnviaFacturaPDF: false,
      UsuarioCrea: 'ADMIN'
    });
    
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentCliente(record);
    
    const formValues = {
      Codigo: record.Codigo,
      Nombre: record.Nombre || '',
      Telefono: record.Telefono || '',
      Pais: record.Pais || '',
      Direccion: record.Direccion || '',
      Poblacion: record.Poblacion || '',
      CodPostal: record.CodPostal || '',
      NIF: record.NIF || '',
      Provincia: record.Provincia || '',
      EnviaFacturaPDF: record.EnviaFacturaPDF || false,
      SeriesEnviaFacturaPDF: record.SeriesEnviaFacturaPDF || '',
      email: record.email || '',
      UsuarioCrea: record.UsuarioCrea || 'ADMIN'
    };
    
    form.setFieldsValue(formValues);
    setModalVisible(true);
  };

  const handleView = (record) => {
    Modal.info({
      title: `Cliente ${record.Codigo}`,
      width: 700,
      icon: <TeamOutlined />,
      content: (
        <div style={{ marginTop: 20 }}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card size="small" style={{ backgroundColor: '#f0f5ff' }}>
                <Row gutter={[16, 8]}>
                  <Col span={8}>
                    <Text type="secondary">Código</Text>
                    <div style={{ marginTop: 4 }}>
                      <Tag color="blue" style={{ fontSize: '18px', padding: '6px 12px' }}>
                        {record.Codigo}
                      </Tag>
                    </div>
                  </Col>
                  <Col span={16}>
                    <Text type="secondary">Nombre / Razón Social</Text>
                    <div style={{ 
                      marginTop: 4, 
                      padding: '8px 12px', 
                      backgroundColor: '#fff', 
                      borderRadius: '6px',
                      fontSize: '15px',
                      fontWeight: 'bold'
                    }}>
                      {record.Nombre}
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>
            
            <Col span={12}>
              <Card size="small" title="Información de Contacto">
                {record.Telefono && (
                  <div style={{ marginBottom: 8 }}>
                    <PhoneOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                    <Text>{record.Telefono}</Text>
                  </div>
                )}
                {record.email && (
                  <div style={{ marginBottom: 8 }}>
                    <MailOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                    <Text>{record.email}</Text>
                  </div>
                )}
                {!record.Telefono && !record.email && (
                  <Text type="secondary">No hay información de contacto</Text>
                )}
              </Card>
            </Col>
            
            <Col span={12}>
              <Card size="small" title="Ubicación">
                {record.Pais && (
                  <div style={{ marginBottom: 8 }}>
                    <GlobalOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                    <Text>{record.Pais}</Text>
                  </div>
                )}
                {record.Poblacion && (
                  <div style={{ marginBottom: 8 }}>
                    <HomeOutlined style={{ color: '#fa8c16', marginRight: 8 }} />
                    <Text>{record.Poblacion}</Text>
                  </div>
                )}
                {record.Provincia && (
                  <div style={{ marginBottom: 8 }}>
                    <EnvironmentOutlined style={{ color: '#722ed1', marginRight: 8 }} />
                    <Text>{record.Provincia}</Text>
                  </div>
                )}
                {!record.Pais && !record.Poblacion && !record.Provincia && (
                  <Text type="secondary">No hay información de ubicación</Text>
                )}
              </Card>
            </Col>
            
            <Col span={24}>
              <Card size="small" title="Dirección">
                {record.Direccion ? (
                  <Text>{record.Direccion}</Text>
                ) : (
                  <Text type="secondary">No hay dirección registrada</Text>
                )}
                {record.CodPostal && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary">Código Postal: </Text>
                    <Tag color="cyan">{record.CodPostal}</Tag>
                  </div>
                )}
              </Card>
            </Col>
            
            <Col span={12}>
              <Card size="small" title="Datos Fiscales">
                {record.NIF ? (
                  <Tag color="purple" style={{ fontSize: '14px', padding: '4px 8px' }}>
                    <IdcardOutlined style={{ marginRight: 4 }} />
                    {record.NIF}
                  </Tag>
                ) : (
                  <Text type="secondary">Sin NIF/CIF</Text>
                )}
              </Card>
            </Col>
            
            <Col span={12}>
              <Card size="small" title="Facturación">
                <div style={{ marginBottom: 4 }}>
                  <Text type="secondary">Envía Factura PDF: </Text>
                  {record.EnviaFacturaPDF ? (
                    <Badge status="success" text="Sí" />
                  ) : (
                    <Badge status="default" text="No" />
                  )}
                </div>
                {record.SeriesEnviaFacturaPDF && (
                  <div>
                    <Text type="secondary">Serie: </Text>
                    <Tag color="orange">{record.SeriesEnviaFacturaPDF}</Tag>
                  </div>
                )}
              </Card>
            </Col>
            
            <Col span={24}>
              <Card size="small" style={{ backgroundColor: '#f6ffed' }}>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  <strong>💡 Información:</strong>
                  <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                    <li><strong>Usuario Crea:</strong> {record.UsuarioCrea || 'N/A'}</li>
                    {record.UsuarioModifica && (
                      <li><strong>Última modificación:</strong> {record.UsuarioModifica}</li>
                    )}
                  </ul>
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      ),
      onOk() {},
    });
  };

  const handleDelete = async (codigo) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BASE_URL}/api/empresas/${codigo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setClientes(clientes.filter(c => c.Codigo !== codigo));
      message.success('Cliente eliminado correctamente');
    } catch (error) {
      console.error('❌ Error eliminando cliente:', error);
      message.error(error.response?.data?.error || 'Error al eliminar el cliente');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('token');

      if (modalType === 'create') {
        await axios.post(
          `${BASE_URL}/api/empresas`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        fetchClientes();
        message.success('Cliente creado correctamente');
      } else {
        await axios.put(
          `${BASE_URL}/api/empresas/${currentCliente.Codigo}`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setClientes(prevClientes => 
          prevClientes.map(cliente => 
            cliente.Codigo === currentCliente.Codigo
              ? { ...cliente, ...values }
              : cliente
          )
        );
        
        message.success('Cliente actualizado correctamente');
      }
      
      setModalVisible(false);
      form.resetFields();
      
    } catch (error) {
      console.error('❌ Error guardando cliente:', error);
      message.error(error.response?.data?.error || 'Error al guardar los datos');
    }
  };

  const handleSearch = () => {
    if (searchText.trim() === '') {
      message.warning('Por favor ingrese un término de búsqueda');
      return;
    }
    setSearchLoading(true);
    fetchClientes({ current: 1 });
  };

  const handleResetSearch = () => {
    setSearchText('');
    setFilteredInfo({});
    setSortedInfo({});
    setSearchLoading(false);
    fetchClientes({ current: 1 });
  };

  const handleExport = () => {
    message.info('Función de exportación en desarrollo');
  };

  const searchBar = (
    <div style={{ marginBottom: 16 }}>
      <Space>
        <Input
          placeholder="Buscar por código, nombre, NIF, población, país..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 400 }}
          onPressEnter={handleSearch}
          allowClear
          onClear={handleResetSearch}
          prefix={<SearchOutlined />}
        />
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleSearch}
          loading={searchLoading}
        >
          Buscar
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={handleResetSearch}
        >
          Restablecer
        </Button>
      </Space>
    </div>
  );

  // Estadísticas
  const estadisticas = (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col span={6}>
        <Statistic 
          title="Total Empresas" 
          value={clientes.length}
          prefix={<TeamOutlined />}
          valueStyle={{ color: '#1890ff' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Países" 
          value={paisesUnicos.length}
          prefix={<GlobalOutlined />}
          valueStyle={{ color: '#52c41a' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Envía Factura PDF" 
          value={clientes.filter(c => c.EnviaFacturaPDF).length}
          suffix={`/ ${clientes.length}`}
          prefix={<CheckCircleOutlined />}
          valueStyle={{ color: '#fa8c16' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Con Email" 
          value={clientes.filter(c => c.email).length}
          suffix={`/ ${clientes.length}`}
          prefix={<MailOutlined />}
          valueStyle={{ color: '#722ed1' }}
        />
      </Col>
    </Row>
  );

  return (
    <>
      <PageContainer
        title="Empresas"
        subtitle={`${clientes.length} empresas registradas`}
        icon={<TeamOutlined />}
        loading={loading}
        onRefresh={() => fetchClientes()}
        onCreate={handleCreate}
        badgeCount={clientes.length}
        badgeStatus="processing"
        extra={
          <Space>
            <Button
              icon={<ExportOutlined />}
              onClick={handleExport}
            >
              Exportar
            </Button>
          </Space>
        }
        cardStyle={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        {/* Estadísticas */}
        {estadisticas}
        
        {/* Barra de búsqueda */}
        {searchBar}
        
        {/* Tabla */}
        <Table
          columns={columns}
          dataSource={clientes}
          rowKey={(record) => record.Codigo}
          loading={loading}
          scroll={{ x: 1400, y: 600 }}
          style={{
            borderRadius: '8px',
            overflow: 'hidden'
          }}
          size="middle"
          locale={{
            emptyText: 'No se encontraron empresas'
          }}
        />
      </PageContainer>

      {/* Modal para crear/editar cliente */}
      <Modal
        title={modalType === 'create' ? 'Nuevo Cliente' : 'Editar Cliente'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={800}
        okText={modalType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
        confirmLoading={loading}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            EnviaFacturaPDF: false,
            UsuarioCrea: 'ADMIN'
          }}
        >
          {/* Primera fila: Código y Nombre */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="Codigo"
                label="Código"
                rules={[
                  { 
                    required: true, 
                    message: 'Por favor ingrese el código',
                    max: 10,
                    whitespace: false
                  }
                ]}
                tooltip="Código único del cliente (máx. 10 caracteres)"
              >
                <Input 
                  placeholder="Ej: 001, CL-001"
                  maxLength={10}
                  disabled={modalType === 'edit'}
                  prefix={<IdcardOutlined />}
                />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                name="Nombre"
                label="Nombre / Razón Social"
                rules={[
                  { 
                    required: true, 
                    message: 'Por favor ingrese el nombre o razón social',
                    max: 200
                  }
                ]}
                tooltip="Nombre completo del cliente o razón social"
              >
                <Input 
                  placeholder="Nombre del cliente"
                  maxLength={200}
                  showCount
                  prefix={<TeamOutlined />}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Segunda fila: País y Población */}
          <Row gutter={16}>
            <Col span={8}>
                    <Form.Item
                      name="Pais"
                      label="Pais"
                    >
                        <Select
                            showSearch
                            placeholder="Seleccione pais"
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                option.label.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {paises.map(pais => (
                                <Option key={pais.Codigo} value={pais.Codigo} label={pais.Descripcion}>
                                    {renderFlag(pais.CodigoISO)}
                                    {pais.Descripcion}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="Poblacion"
                label="Población"
                tooltip="Ciudad o localidad"
              >
                <Input 
                  placeholder="Ciudad"
                  maxLength={100}
                  prefix={<HomeOutlined />}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="Provincia"
                label="Provincia"
                tooltip="Provincia o estado"
              >
                <Input 
                  placeholder="Provincia"
                  maxLength={100}
                  prefix={<EnvironmentOutlined />}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Tercera fila: Dirección y Código Postal */}
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="Direccion"
                label="Dirección"
                tooltip="Dirección completa"
              >
                <Input 
                  placeholder="Calle, número, etc."
                  maxLength={200}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="CodPostal"
                label="Código Postal"
                tooltip="Código postal"
              >
                <Input 
                  placeholder="Código Postal"
                  maxLength={20}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Cuarta fila: Teléfono, NIF y Email */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="Telefono"
                label="Teléfono"
                tooltip="Número de teléfono de contacto"
              >
                <Input 
                  placeholder="Teléfono"
                  maxLength={50}
                  prefix={<PhoneOutlined />}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="NIF"
                label="NIF / CIF"
                tooltip="Identificación fiscal"
              >
                <Input 
                  placeholder="NIF o CIF"
                  maxLength={50}
                  prefix={<IdcardOutlined />}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { 
                    type: 'email', 
                    message: 'Por favor ingrese un email válido'
                  }
                ]}
                tooltip="Correo electrónico de contacto"
              >
                <Input 
                  placeholder="email@ejemplo.com"
                  maxLength={200}
                  prefix={<MailOutlined />}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Quinta fila: EnviaFacturaPDF y Serie */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="EnviaFacturaPDF"
                label="Envía Factura PDF"
                valuePropName="checked"
                tooltip="Indica si se envía factura en formato PDF"
              >
                <Switch
                  checkedChildren="Sí"
                  unCheckedChildren="No"
                  style={{ minWidth: 80 }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="SeriesEnviaFacturaPDF"
                label="Serie PDF"
                tooltip="Serie para facturas PDF"
              >
                <Input 
                  placeholder="Ej: A, B, R"
                  maxLength={10}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Sexta fila: UsuarioCrea */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="UsuarioCrea"
                label="Usuario Crea"
                tooltip="Usuario que crea el registro"
              >
                <Input 
                  placeholder="ADMIN"
                  maxLength={50}
                  disabled={modalType === 'edit'}
                />
              </Form.Item>
            </Col>
          </Row>

          {modalType === 'create' && (
            <Card size="small" style={{ marginTop: 16, backgroundColor: '#f6ffed' }}>
              <div style={{ fontSize: '13px', color: '#666' }}>
                <strong>📝 Información importante:</strong>
                <ul style={{ marginTop: 8, marginBottom: 4, paddingLeft: 20 }}>
                  <li><strong>Código:</strong> Identificador único del cliente (máx. 10 caracteres)</li>
                  <li><strong>Nombre:</strong> Razón social o nombre completo del cliente</li>
                  <li><strong>NIF/CIF:</strong> Identificación fiscal para facturación</li>
                  <li><strong>Envía Factura PDF:</strong> Activar si el cliente recibe facturas en PDF</li>
                  <li><strong>Serie PDF:</strong> Serie específica para facturas de este cliente</li>
                </ul>
              </div>
            </Card>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default Empresas;